import React, { useEffect, useState } from "react";
import { CalendarRange, Download } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../../lib/api";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const COLS = "grid grid-cols-2 md:grid-cols-[90px_110px_60px_90px_110px_110px_90px_100px] gap-2 md:gap-4";
const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "short", year: "numeric" });
const years = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2].map(String); };

const exportCsv = (rows, year) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Month", "Vehicle", "Fills", "Litres", "Spend", "Km", "Km source", "Km/L", "Cost per km"].join(","),
    ...rows.map((r) => [r.month, r.vehicle_number, r.fills, r.litres, r.amount, r.km, r.km_source, r.km_per_litre, r.cost_per_km].map(esc).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `fuel_monthly_${year}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

const Row = ({ r }) => (
  <div data-testid={`fuel-month-${r.month}-${r.vehicle_number}`} className={`${COLS} py-2.5 text-sm items-center`}>
    <span className="num text-xs font-semibold">{monthName(r.month)}</span>
    <span className="num font-semibold">{r.vehicle_number}</span>
    <span className="num text-xs">{r.fills} fill{r.fills === 1 ? "" : "s"}</span>
    <span className="num font-bold">{r.litres} L</span>
    <span className="num">{r.amount ? inr(r.amount) : "—"}</span>
    <span className="num">{r.km ? `${r.km.toLocaleString("en-IN")} km` : "—"}{r.km_source === "trips" && <span className="text-[10px] text-muted-foreground"> (trips)</span>}</span>
    <span className="num text-xs font-semibold text-primary">{r.km_per_litre ? `${r.km_per_litre} km/L` : "—"}</span>
    <span data-testid={`fuel-month-cpk-${r.month}-${r.vehicle_number}`} className="num text-xs font-semibold">{r.cost_per_km ? `${inr(r.cost_per_km)}/km` : "—"}</span>
  </div>
);

export const MonthlyFuelReport = ({ vehicle, refreshKey }) => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/gatepass/fuel/monthly", { params: { year, ...(vehicle ? { vehicle } : {}) } })
      .then((r) => setRows(r.data.rows)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [year, vehicle, refreshKey]);
  const tot = rows.reduce((t, r) => ({ litres: t.litres + r.litres, amount: t.amount + r.amount, km: t.km + r.km, kmAmt: t.kmAmt + (r.km ? r.amount : 0) }), { litres: 0, amount: 0, km: 0, kmAmt: 0 });

  return (
    <section data-testid="fuel-monthly" className="rounded-xl border border-border bg-card p-4 mt-6 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-base md:text-lg font-semibold flex items-center gap-2"><CalendarRange size={16} /> Monthly fuel report</h3>
          <p className="text-xs text-muted-foreground">Km come from fuel odometer readings; if none, from gate pass trips (marked "trips").</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="fuel-month-year" className={ctl} value={year} onChange={(e) => setYear(e.target.value)}>
            {years().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="fuel-month-export" onClick={() => exportCsv(rows, year)} className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors"><Download size={14} /> CSV</button>
        </div>
      </div>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">No fuel or trips in {year}.</div> : (<>
        <div className={`${COLS} hidden md:grid text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
          <span>Month</span><span>Vehicle</span><span>Fills</span><span>Litres</span><span>Spend</span><span>Km</span><span>Km/L</span><span>Cost/km</span>
        </div>
        <div className="divide-y divide-border">{rows.map((r) => <Row key={`${r.month}-${r.vehicle_number}`} r={r} />)}</div>
        <div data-testid="fuel-month-total" className="text-sm font-semibold border-t border-border pt-2 num">
          {year} total: {tot.litres.toFixed(2)} L · {inr(tot.amount)} · {tot.km.toLocaleString("en-IN")} km{tot.km && tot.kmAmt ? ` · ${inr(tot.kmAmt / tot.km)}/km` : ""}
        </div>
      </>)}
    </section>
  );
};
